import { Component, EventEmitter, Input, Output } from '@angular/core'
import { TreeNode } from 'primeng/api'
import { ClipboardService } from 'src/app/services/clipboard.service'

export interface TreeNodeWithTooltip<T> extends TreeNode<T> {
  shouldDisplayTooltip: boolean
  tooltip?: string
  children?: TreeNodeWithTooltip<T>[]
  parent?: TreeNodeWithTooltip<T>
  level: number
  isTransitivelySelected?: boolean
  isAncestorDirectlySelected?: boolean
  isExcluded?: boolean
  hasDependeeResourceValues?: boolean
  dependeeArns?: string[]
  isWeakDependency?: boolean
}

@Component({
  selector: 'tree-node',
  templateUrl: './tree-node.component.html',
  styleUrls: ['./tree-node.component.scss'],
  standalone: false,
})
/**
 * TreeNodeComponent is a component that represents a node in a tree structure. This was adapted from the PrimeNG
 * TreeNode component to add support for tooltips that are displayed when hovering over the selection checkboxes.
 * Unlike the PrimeNG tree component, this component does not recursively render child nodes. Instead, it is
 * expected that the parent component will render all visible/expanded nodes in the tree, where the `level`
 * property is set on each node to indicate the depth of the node in the tree.  This allows for large trees to be
 * rendered within a virtual scroll container.
 */
export class TreeNodeComponent {
  @Input() node: TreeNodeWithTooltip<any>
  @Input() parentNode?: TreeNodeWithTooltip<any>
  @Input() readonly: boolean = false

  @Output() selectionChange = new EventEmitter<TreeNodeWithTooltip<any>>()
  @Output() expand = new EventEmitter<TreeNodeWithTooltip<any>>()
  @Output() collapse = new EventEmitter<TreeNodeWithTooltip<any>>()

  private tooltipHideTimer: any

  constructor(private clipboard: ClipboardService) {}

  toggleExpanded() {
    if (!this.node.loading) {
      this.node.expanded = !this.node.expanded
      if (this.node.expanded) {
        this.expand.emit(this.node)
      } else {
        this.collapse.emit(this.node)
      }
    }
  }

  onSelectionChange(event) {
    if (this.node.partialSelected) {
      // If a node is partially selected and then clicked, the default behavior for the browser is to mark the
      // checkbox as not checked.  We want to override this behavior and set it to checked.
      event.target.checked = true
    }

    if (!event.target.checked && (this.node.isTransitivelySelected || this.node.isAncestorDirectlySelected)) {
      // If the node is unchecked, but is still transitively selected, keep it marked checked
      event.target.checked = true
    }

    this.selectionChange.emit(this.node)
  }

  keepTooltipVisible() {
    if (this.tooltipHideTimer) {
      clearTimeout(this.tooltipHideTimer)
      this.tooltipHideTimer = null
    }
  }

  hideTooltipDelayed() {
    this.tooltipHideTimer = setTimeout(() => {
      // Logic to hide tooltip - this depends on the tooltip implementation
      // For now, we'll let the PrimeNG tooltip handle it naturally
    }, 200)
  }

  copyDependeeArns() {
    if (this.node.dependeeArns?.length) {
      this.clipboard.copy(this.node.dependeeArns.join('\n'))
    }
  }

  copySpecificArns(arns: string[]) {
    if (arns?.length) {
      this.clipboard.copy(arns.join('\n'))
    }
  }
}
